// src/pages/admin/AdminUnitPages.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { collection, doc, getDocs, getDoc, updateDoc, orderBy, query } from 'firebase/firestore';
import { ref as sRef, getDownloadURL } from 'firebase/storage';
import { db, storage, uploadFileWithToken } from '../../lib/firebase';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore } from '../../stores/useThemeStore';

interface BookEntry { id: string; title: string; units: number; }

const inputStyle = (C: ReturnType<typeof useThemePalette>, isDark: boolean): React.CSSProperties => ({
  padding: '9px 12px', borderRadius: '10px',
  border: `1px solid ${C.border}`,
  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  color: C.text, fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none',
});

export default function AdminUnitPages() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [books,     setBooks]     = useState<BookEntry[]>([]);
  const [bookId,    setBookId]    = useState('');
  const [unitNum,   setUnitNum]   = useState('1');
  const [unitCount, setUnitCount] = useState(1);
  const [pages,     setPages]     = useState<string[]>([]);
  const [newFiles,  setNewFiles]  = useState<File[]>([]);
  const [status,    setStatus]    = useState('');
  const [isErr,     setIsErr]     = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'books'), orderBy('displayOrder')))
      .then(snap => {
        const list: BookEntry[] = [];
        snap.forEach(d => list.push({ id: d.id, title: d.data().title as string, units: d.data().units as number ?? 0 }));
        setBooks(list);
      })
      .catch(() => {});
  }, []);

  const loadPages = useCallback(() => {
    if (!bookId) return;
    getDoc(doc(db, 'books', bookId))
      .then(snap => {
        if (!snap.exists()) return;
        const data = snap.data();
        const unitKey = `unit${unitNum}`;
        const existing = (data.unitPages ?? {})[unitKey] as string[] | undefined;
        setPages(existing ?? []);
      })
      .catch(() => {});
  }, [bookId, unitNum]);

  useEffect(() => {
    const book = books.find(b => b.id === bookId);
    setUnitCount(book?.units ?? 1);
    setPages([]);
    setNewFiles([]);
    loadPages();
  }, [bookId, unitNum, books, loadPages]);

  const uploadAndSave = async () => {
    if (!bookId) { setStatus('Select a book'); setIsErr(true); return; }
    if (newFiles.length === 0) { setStatus('Select files to upload'); setIsErr(true); return; }
    setUploading(true); setStatus('Uploading...'); setIsErr(false);
    try {
      const uploadedUrls: string[] = [];
      for (const file of newFiles) {
        const path = `unitPages/${bookId}/unit${unitNum}/${Date.now()}_${file.name}`;
        const storageRef = sRef(storage, path);
        await uploadFileWithToken(storageRef, file);
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      }
      const merged = [...pages, ...uploadedUrls];
      await updateDoc(doc(db, 'books', bookId), { [`unitPages.unit${unitNum}`]: merged });
      setPages(merged);
      setNewFiles([]);
      setStatus(`Uploaded ${uploadedUrls.length} page(s)!`); setIsErr(false);
      setTimeout(() => setStatus(''), 3000);
    } catch (e: unknown) {
      setStatus(e instanceof Error ? e.message : 'Upload failed'); setIsErr(true);
    } finally {
      setUploading(false);
    }
  };

  const removePage = async (idx: number) => {
    const updated = pages.filter((_, i) => i !== idx);
    try {
      await updateDoc(doc(db, 'books', bookId), { [`unitPages.unit${unitNum}`]: updated });
      setPages(updated);
    } catch {
      setStatus('Failed to remove page'); setIsErr(true);
    }
  };

  const moveUp = async (idx: number) => {
    if (idx === 0) return;
    const updated = [...pages];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    await updateDoc(doc(db, 'books', bookId), { [`unitPages.unit${unitNum}`]: updated }).catch(() => {});
    setPages(updated);
  };

  const moveDown = async (idx: number) => {
    if (idx === pages.length - 1) return;
    const updated = [...pages];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    await updateDoc(doc(db, 'books', bookId), { [`unitPages.unit${unitNum}`]: updated }).catch(() => {});
    setPages(updated);
  };

  const unitOptions = Array.from({ length: unitCount }, (_, i) => i + 1);

  return (
    <div style={{ padding: '32px', maxWidth: '760px' }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: C.text, marginBottom: '8px' }}>Unit Pages</div>
      <div style={{ fontSize: '0.8rem', color: C.muted, marginBottom: '28px' }}>Upload WebP page images for each unit</div>

      {/* Selection */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <select
          value={bookId}
          onChange={e => { setBookId(e.target.value); setUnitNum('1'); setPages([]); setNewFiles([]); }}
          style={{ ...inputStyle(C, isDark), flex: '1 1 180px' }}
        >
          <option value="">— Select Book —</option>
          {books.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
        </select>

        <select
          value={unitNum}
          onChange={e => setUnitNum(e.target.value)}
          disabled={!bookId}
          style={{ ...inputStyle(C, isDark), flex: '0 0 110px' }}
        >
          {unitOptions.map(n => <option key={n} value={String(n)}>Unit {n}</option>)}
        </select>
      </div>

      {bookId && (
        <>
          {/* Existing pages */}
          {pages.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: C.text, marginBottom: '12px' }}>
                Current Pages ({pages.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pages.map((url, idx) => (
                  <div key={url} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    background: C.card, border: `1px solid ${C.border}`,
                    borderRadius: '10px', padding: '10px 14px',
                  }}>
                    <img src={url} alt={`Page ${idx + 1}`} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: '0.78rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Page {idx + 1}
                    </span>
                    <button onClick={() => void moveUp(idx)} disabled={idx === 0}
                      style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '3px 8px', color: C.muted, cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit' }}>
                      ↑
                    </button>
                    <button onClick={() => void moveDown(idx)} disabled={idx === pages.length - 1}
                      style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '3px 8px', color: C.muted, cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit' }}>
                      ↓
                    </button>
                    <button onClick={() => void removePage(idx)}
                      style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: '6px', padding: '3px 10px', color: C.error, cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit' }}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload new */}
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '24px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 700, color: C.text, display: 'block', marginBottom: '8px' }}>
              Add Pages (WebP / PNG / JPG)
            </label>
            <input
              type="file"
              accept="image/webp,image/png,image/jpeg,image/*"
              multiple
              onChange={e => setNewFiles(Array.from(e.target.files ?? []))}
              style={{ fontSize: '0.82rem', color: C.text, marginBottom: '12px', display: 'block' }}
            />
            {newFiles.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: C.premium, marginBottom: '12px' }}>
                {newFiles.length} file(s) selected
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => void uploadAndSave()}
                disabled={uploading || newFiles.length === 0}
                style={{
                  padding: '8px 20px', borderRadius: '999px', border: 'none',
                  background: (uploading || newFiles.length === 0) ? C.border : C.accent,
                  color: (uploading || newFiles.length === 0) ? C.muted : '#fff',
                  fontSize: '0.78rem', fontWeight: 700, fontFamily: 'inherit',
                  cursor: (uploading || newFiles.length === 0) ? 'not-allowed' : 'pointer',
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Pages'}
              </button>
              {status && <span style={{ fontSize: '0.78rem', color: isErr ? C.error : C.success }}>{status}</span>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
